import type {
  BindingConfig,
  EditorSDK,
  Role,
} from '@wix/wix-data-client-common-standalone'
import type { AppApi } from '@wix/wix-data-client-common'
import type {
  DataBindingComponentModel,
  DataBindingPanelCommonSection,
  DataBindingPanelSection,
  SubroleCondition,
} from '@wix/data-binding-registry-sdk'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import { getComponentTypeData } from '../../business-logic/components/getComponentTypeData'
import { getBindingDefinitions } from '../../business-logic/bindings/componentBindingInfo'

const getSubroleDefinitions = async ({
  editorSdkProxy,
  componentRef,
  role,
}: {
  editorSdkProxy: EditorSDK
  role: Role
  componentRef: ComponentRef
}): Promise<DataBindingPanelCommonSection['subroles']> => {
  const appApi = await editorSdkProxy.editor.getAppAPI<AppApi>()
  const { sdkType: componentType, viewerType: componentViewerType } =
    await getComponentTypeData({
      editorSdkProxy,
      componentRef,
    })

  const bindingDefinitions: DataBindingComponentModel = (
    await getBindingDefinitions({
      editorSdkProxy,
      appApi,
      componentType,
      componentViewerType,
      componentRef,
    })
  ).getOrElse(null)

  return bindingDefinitions?.sections.find(
    (section: DataBindingPanelSection) => section.role === role,
  )?.subroles
}

const satisfiesCondition = (
  condition: SubroleCondition,
  bindings: BindingConfig[],
) => {
  const {
    connectedProps = [],
    connectedEvents = [],
    connectedBehaviors = [],
  } = condition
  const allPropsConnected = connectedProps.every(({ prop, fieldType }) =>
    bindings.some(
      binding =>
        binding.type === 'Field' &&
        binding.prop === prop &&
        (!fieldType || fieldType === binding.fieldType),
    ),
  )

  const allEventsConnected = connectedEvents.every(({ event, action, prop }) =>
    bindings.some(
      binding =>
        binding.type === 'Action' &&
        binding.event === event &&
        // @ts-expect-error TODO fix types
        (!prop || prop === binding.prop) &&
        (!action || action === binding.action),
    ),
  )

  const allBehaviorsConnected = connectedBehaviors.every(({ behavior }) =>
    bindings.some(
      binding => binding.type === 'Behavior' && binding.behavior === behavior,
    ),
  )

  return allPropsConnected && allEventsConnected && allBehaviorsConnected
}

interface Params {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  role: Role
  componentRef: ComponentRef
  bindings: BindingConfig[]
}

export const getSubrole = async ({
  editorSdkProxy,
  componentRef,
  role,
  bindings,
}: Params): Promise<string | undefined> => {
  const subroleDefinitions = await getSubroleDefinitions({
    editorSdkProxy,
    componentRef,
    role,
  })

  const validSubrole = subroleDefinitions?.find(({ conditions }) =>
    conditions.some(condition => satisfiesCondition(condition, bindings)),
  )

  return validSubrole?.name
}
